// services/admin/adminCarteira.service.ts
// Carteira Intrex pelo lado do admin: saldo, extrato, recargas e ajuste manual.
import { ADMIN_API } from "./adminApi";

export type LancamentoAdmin = {
  id: number;
  tipo: string;
  valor: number;
  valor_centavos: number;
  descricao: string | null;
  referencia_tipo: string | null;
  referencia_id: number | null;
  chave: string;
  criado_em: string;
};

export type RecargaAdmin = {
  id: number;
  finalidade: "recarga" | "cotacao";
  metodo: "PIX" | "CREDIT_CARD";
  valor: number;
  taxa: number;
  total: number;
  status: "PENDING" | "PAGO" | "ESTORNADO" | "FALHOU" | "CANCELADO";
  invoice_url: string | null;
  asaas_payment_id?: string | null;
  criado_em: string;
  pago_em: string | null;
};

export type BoletoAdmin = {
  id: number;
  cliente_id: number;
  cotacao_id: number | null;
  valor: number;
  vencimento: string | null;
  status: string | null;
  asaas_payment_id: string | null;
  bank_slip_url: string | null;
  criado_em: string;
  atualizado_em: string;
  /** Boleto em lote cobre várias cotações e não guarda vínculo com nenhuma. */
  em_lote: boolean;
  cliente: { id: number; razaoSocial: string; emailPrincipal: string } | null;
  envio: { pedido_ref: string; carrier: string; status_norm: string | null } | null;
};

export type BoletosResposta = {
  ok: true;
  page: number;
  pageSize: number;
  total: number;
  pendentes_vencidos: number;
  boletos: BoletoAdmin[];
};

export const listBoletos = async (
  filtros: { page?: number; pageSize?: number; status?: string; cliente_id?: string | number } = {}
) => {
  const params = Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== "" && v !== undefined && v !== null)
  );
  const { data } = await ADMIN_API.get<BoletosResposta>("/api/admin/carteira/boletos", { params });
  return data;
};

/**
 * Força a baixa do boleto pelo Asaas. Era o único caminho sem saída manual: o
 * status do boleto só era atualizado pelo webhook.
 */
export const sincronizarBoleto = async (id: string | number) => {
  const { data } = await ADMIN_API.post<{
    ok: true;
    mudou: boolean;
    status_anterior: string | null;
    boleto: BoletoAdmin;
    remessa_paga: boolean;
  }>(`/api/admin/carteira/boletos/${id}/sincronizar`);
  return data;
};

export type CarteiraDoCliente = {
  ok: true;
  cliente: { id: number; razaoSocial: string; emailPrincipal: string };
  habilitada: boolean;
  habilitada_por_coluna: boolean;
  habilitada_em: string | null;
  saldo: number;
  saldo_centavos: number;
  totais: {
    creditado: number;
    debitado: number;
    lancamentos: number;
    truncado: boolean;
  };
  lancamentos: LancamentoAdmin[];
  pagamentos: RecargaAdmin[];
  boletos: BoletoAdmin[];
};

export const getCarteiraDoCliente = async (clienteId: string | number) => {
  const { data } = await ADMIN_API.get<CarteiraDoCliente>(
    `/api/admin/carteira/clientes/${clienteId}`
  );
  return data;
};

export type RecargaComCliente = RecargaAdmin & {
  cliente: { id: number; razaoSocial: string | null; emailPrincipal: string | null };
};

export type RecargasResposta = {
  ok: true;
  page: number;
  pageSize: number;
  total: number;
  pendentes_antigos: number;
  recargas: RecargaComCliente[];
};

export type RecargaFiltros = {
  page?: number;
  pageSize?: number;
  status?: string;
  metodo?: string;
  cliente_id?: string | number;
};

export const listRecargas = async (filtros: RecargaFiltros = {}) => {
  const params = Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== "" && v !== undefined && v !== null)
  );
  const { data } = await ADMIN_API.get<RecargasResposta>("/api/admin/carteira/recargas", {
    params,
  });
  return data;
};

/** Relê o status no Asaas. Não cria dinheiro: resolve o PIX cujo webhook falhou. */
export const sincronizarRecarga = async (id: string | number) => {
  const { data } = await ADMIN_API.post<{
    ok: true;
    mudou: boolean;
    status_anterior: string;
    recarga: RecargaAdmin;
    saldo: number;
  }>(`/api/admin/carteira/recargas/${id}/sincronizar`);
  return data;
};

/** Crédito (valor positivo) ou débito (negativo). Só role `admin` no backend. */
export const ajustarCarteira = async (
  clienteId: string | number,
  valor: number,
  motivo: string
) => {
  const { data } = await ADMIN_API.post<{
    ok: true;
    lancamento: LancamentoAdmin | null;
    saldo: number;
    saldo_centavos: number;
  }>(`/api/admin/carteira/clientes/${clienteId}/ajuste`, { valor, motivo });
  return data;
};

export const definirCarteiraHabilitada = async (
  clienteId: string | number,
  habilitada: boolean
) => {
  const { data } = await ADMIN_API.patch<{
    ok: true;
    habilitada_por_coluna: boolean;
    habilitada: boolean;
    aviso: string | null;
  }>(`/api/admin/carteira/clientes/${clienteId}/habilitada`, { habilitada });
  return data;
};
